<template>
    <main class="g11" dir="rtl">
        <!-- 1. Hero -->
        <section class="g11-hero">
            <div class="g11-hero__bg" aria-hidden="true">
                <span class="g11-hero__bg-code g11-hero__bg-code--1">{ }</span>
                <span class="g11-hero__bg-code g11-hero__bg-code--2">0101</span>
            </div>

            <div class="g11-wrap g11-hero__grid">
                <div class="g11-hero__content">
                    <span class="g11-badge g11-badge--green">{{ hero.badge }}</span>
                    <h1 class="seo-sr-only">تانية ثانوي Programming &amp; AI</h1>
                    <p class="g11-hero__title">{{ hero.title }}</p>
                    <p class="g11-hero__subtitle">{{ hero.subtitle }}</p>

                    <div class="g11-hero__actions">
                        <router-link :to="coursesRoute" class="g11-btn g11-btn--primary">
                            <span>{{ hero.primaryCta }}</span>
                            <lucide-icon name="ArrowLeft" class="g11-btn__icon" />
                        </router-link>
                        <a
                            :href="whatsappUrl"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="g11-btn g11-btn--outline"
                        >
                            {{ hero.secondaryCta }}
                        </a>
                    </div>

                    <div class="g11-hero__chips">
                        <span v-for="chip in hero.chips" :key="chip" class="g11-chip">{{ chip }}</span>
                    </div>
                </div>

                <div class="g11-hero__visual">
                    <div class="g11-hero__glow" aria-hidden="true" />
                    <span class="g11-hero__visual-code" aria-hidden="true">AI</span>
                    <div class="g11-hero__dashboard" aria-hidden="true">
                        <div class="g11-mini-window__bar">
                            <span />
                            <span />
                            <span />
                        </div>
                        <div class="g11-hero__dashboard-row">
                            <span>شرح</span>
                            <strong>متاح</strong>
                        </div>
                        <div class="g11-hero__dashboard-row">
                            <span>تدريب</span>
                            <strong>مستمر</strong>
                        </div>
                        <div class="g11-progress">
                            <span style="width: 74%" />
                        </div>
                    </div>
                    <img
                        :src="heroImageSrc"
                        alt="محمد غنيم — البرمجة مع غنيم"
                        class="g11-hero__img"
                        width="480"
                        height="640"
                        loading="eager"
                        @error="onHeroImageError"
                    />
                </div>
            </div>
        </section>

        <!-- Intro video -->
        <section class="g11-section g11-section--video" aria-labelledby="g11-video-title">
            <div class="g11-wrap">
                <header class="g11-section__header g11-section__header--tight g11-section__header--center">
                    <h2 id="g11-video-title" class="g11-section__title">{{ introVideo.title }}</h2>
                    <p class="g11-section__subtitle">{{ introVideo.subtitle }}</p>
                </header>

                <div class="g11-video">
                    <div class="g11-video__frame">
                        <iframe
                            class="g11-video__iframe"
                            :src="youtubeEmbedUrl"
                            title="فيديو تعريفي — تانية ثانوي Programming & AI | البرمجة مع غنيم"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowfullscreen
                            loading="lazy"
                            referrerpolicy="strict-origin-when-cross-origin"
                        />
                    </div>
                    <a
                        class="g11-video__open"
                        :href="introVideo.watchUrl"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <lucide-icon name="Youtube" :size="18" aria-hidden="true" />
                        مشاهدة على يوتيوب
                    </a>
                </div>
            </div>
        </section>

        <!-- 2. Why -->
        <section class="g11-section g11-section--soft">
            <div class="g11-wrap">
                <div class="g11-split g11-split--why">
                    <div>
                        <header class="g11-section__header">
                            <h2 class="g11-section__title">{{ why.title }}</h2>
                            <p class="g11-section__subtitle">{{ why.subtitle }}</p>
                        </header>
                        <div class="g11-grid g11-grid--2">
                            <article v-for="card in why.cards" :key="card.title" class="g11-card g11-card--accent">
                                <h3 class="g11-card__title">{{ card.title }}</h3>
                                <p class="g11-card__text">{{ card.text }}</p>
                            </article>
                        </div>
                    </div>

                    <aside class="g11-system-visual" aria-label="منظومة تانية ثانوي">
                        <div class="g11-system-visual__center">
                            <span>نظام</span>
                            <strong>تانية ثانوي</strong>
                        </div>
                        <span class="g11-system-visual__node g11-system-visual__node--1">شرح</span>
                        <span class="g11-system-visual__node g11-system-visual__node--2">تطبيق</span>
                        <span class="g11-system-visual__node g11-system-visual__node--3">مراجعة</span>
                        <span class="g11-system-visual__node g11-system-visual__node--4">أسئلة</span>
                    </aside>
                </div>
                <p class="g11-strong-line">{{ why.closing }}</p>
            </div>
        </section>

        <!-- 3. Coverage -->
        <section class="g11-section">
            <div class="g11-wrap">
                <header class="g11-section__header">
                    <h2 class="g11-section__title">{{ coverage.title }}</h2>
                    <p class="g11-section__subtitle">{{ coverage.subtitle }}</p>
                </header>
                <div class="g11-coverage-strip" aria-label="خريطة تغطية المنهج">
                    <div class="g11-coverage-strip__item">
                        <span>01</span>
                        <strong>شرح</strong>
                    </div>
                    <div class="g11-coverage-strip__line" />
                    <div class="g11-coverage-strip__item">
                        <span>02</span>
                        <strong>تدريب</strong>
                    </div>
                    <div class="g11-coverage-strip__line" />
                    <div class="g11-coverage-strip__item">
                        <span>03</span>
                        <strong>متابعة</strong>
                    </div>
                    <div class="g11-coverage-strip__line" />
                    <div class="g11-coverage-strip__item">
                        <span>04</span>
                        <strong>امتحان</strong>
                    </div>
                </div>
                <div class="g11-grid g11-grid--coverage">
                    <article
                        v-for="(item, index) in coverage.items"
                        :key="item.title"
                        class="g11-feature"
                    >
                        <span class="g11-feature__num">{{ index + 1 }}</span>
                        <h3 class="g11-feature__title">{{ item.title }}</h3>
                        <p class="g11-feature__text">{{ item.text }}</p>
                    </article>
                </div>
                <p class="g11-curriculum-link">
                    <router-link :to="{ name: 'grade-11-programming-ai-curriculum' }">
                        تعرف على فصول منهج البرمجة والذكاء الاصطناعي تانية ثانوي بالتفصيل
                    </router-link>
                </p>
            </div>
        </section>

        <!-- 3b. Training proof -->
        <section class="g11-section g11-section--proof" aria-labelledby="g11-proof-title">
            <div class="g11-wrap">
                <header class="g11-section__header g11-section__header--center">
                    <h2 id="g11-proof-title" class="g11-section__title">{{ proof.title }}</h2>
                    <p class="g11-section__subtitle">{{ proof.subtitle }}</p>
                </header>
                <div class="g11-proof">
                    <div class="g11-proof__hero">
                        <strong class="ltr-inline">{{ proof.hero.value }}</strong>
                        <span>{{ proof.hero.label }}</span>
                        <small>{{ proof.hero.context }}</small>
                    </div>
                    <div class="g11-proof__grid">
                        <article v-for="stat in proof.stats" :key="stat.label" class="g11-card">
                            <strong>{{ stat.value }}</strong>
                            <h3 class="g11-card__title">{{ stat.label }}</h3>
                            <p class="g11-card__text">{{ stat.text }}</p>
                        </article>
                    </div>
                </div>
                <p class="g11-proof__note">{{ proof.note }}</p>
            </div>
        </section>

        <!-- 4. Journey -->
        <section class="g11-section g11-section--soft">
            <div class="g11-wrap">
                <header class="g11-section__header">
                    <h2 class="g11-section__title">{{ journey.title }}</h2>
                    <p class="g11-section__subtitle">{{ journey.subtitle }}</p>
                </header>
                <ol class="g11-roadmap">
                    <li
                        v-for="(step, index) in journey.steps"
                        :key="step.title"
                        class="g11-roadmap__step"
                    >
                        <div class="g11-roadmap__marker">
                            <span class="g11-roadmap__num">{{ index + 1 }}</span>
                        </div>
                        <article class="g11-card g11-card--roadmap">
                            <h3 class="g11-card__title">{{ step.title }}</h3>
                            <p class="g11-card__text">{{ step.text }}</p>
                        </article>
                    </li>
                </ol>
                <p class="g11-strong-line">{{ journey.closing }}</p>
            </div>
        </section>

        <!-- 5. Tools -->
        <section class="g11-section">
            <div class="g11-wrap">
                <div class="g11-split g11-split--tools">
                    <div>
                        <header class="g11-section__header">
                            <h2 class="g11-section__title">{{ tools.title }}</h2>
                            <p class="g11-section__subtitle">{{ tools.subtitle }}</p>
                        </header>
                        <div class="g11-grid g11-grid--2">
                            <article v-for="card in tools.cards" :key="card.title" class="g11-card g11-card--tool">
                                <h3 class="g11-card__title">{{ card.title }}</h3>
                                <p class="g11-card__text">{{ card.text }}</p>
                            </article>
                        </div>
                    </div>

                    <aside class="g11-lab-mockup" aria-label="شكل مبسط لأدوات التدريب">
                        <div class="g11-mini-window__bar">
                            <span />
                            <span />
                            <span />
                        </div>
                        <div class="g11-lab-mockup__code">
                            <span>lesson.start()</span>
                            <span>student.practice()</span>
                            <span>review.beforeExam()</span>
                        </div>
                        <div class="g11-lab-mockup__output">
                            <strong>نتيجة التدريب</strong>
                            <div class="g11-progress">
                                <span style="width: 82%" />
                            </div>
                            <p>الفهم بيتثبت بالتطبيق والمراجعة.</p>
                        </div>
                    </aside>
                </div>
            </div>
        </section>

        <!-- 6. Calendar -->
        <section class="g11-section g11-section--soft">
            <div class="g11-wrap g11-calendar">
                <div class="g11-calendar__main">
                    <header class="g11-section__header g11-section__header--tight">
                        <h2 class="g11-section__title">{{ calendar.title }}</h2>
                        <p class="g11-section__subtitle">{{ calendar.subtitle }}</p>
                    </header>
                    <ul class="g11-bullets">
                        <li v-for="item in calendar.bullets" :key="item">{{ item }}</li>
                    </ul>
                </div>
                <aside class="g11-highlight-card">
                    <div class="g11-calendar-mockup" aria-hidden="true">
                        <div class="g11-calendar-mockup__head">
                            <span>خطة الأسبوع</span>
                            <strong>منظم</strong>
                        </div>
                        <div class="g11-calendar-mockup__row">
                            <span>درس</span>
                            <b />
                            <b />
                            <b class="is-muted" />
                        </div>
                        <div class="g11-calendar-mockup__row">
                            <span>واجب</span>
                            <b />
                            <b class="is-muted" />
                            <b />
                        </div>
                        <div class="g11-calendar-mockup__row">
                            <span>مراجعة</span>
                            <b class="is-muted" />
                            <b />
                            <b />
                        </div>
                    </div>
                    <p>{{ calendar.highlight }}</p>
                </aside>
            </div>
        </section>

        <!-- 7. Parents -->
        <section class="g11-section">
            <div class="g11-wrap g11-parents">
                <div class="g11-split g11-split--parents">
                    <div>
                        <header class="g11-section__header g11-section__header--tight">
                            <h2 class="g11-section__title">{{ parents.title }}</h2>
                        </header>
                        <p class="g11-parents__text">{{ parents.text }}</p>
                        <ul class="g11-bullets g11-bullets--check">
                            <li v-for="point in parents.points" :key="point">{{ point }}</li>
                        </ul>
                        <p class="g11-highlight-inline">{{ parents.highlight }}</p>
                    </div>
                    <aside class="g11-parent-dashboard" aria-label="ملخص يطمن ولي الأمر">
                        <div class="g11-parent-dashboard__row">
                            <span>المنهج</span>
                            <strong>الأولوية</strong>
                        </div>
                        <div class="g11-parent-dashboard__row">
                            <span>التدريب</span>
                            <strong>مستمر</strong>
                        </div>
                        <div class="g11-parent-dashboard__row">
                            <span>المراجعة</span>
                            <strong>طول السنة</strong>
                        </div>
                        <div class="g11-parent-dashboard__note">نظام واضح بدل مذاكرة عشوائية</div>
                    </aside>
                </div>
            </div>
        </section>

        <!-- 8. FAQ -->
        <section class="g11-section g11-section--soft">
            <div class="g11-wrap">
                <header class="g11-section__header">
                    <h2 class="g11-section__title">{{ faq.title }}</h2>
                </header>
                <div class="g11-faq">
                    <div
                        v-for="(item, index) in faq.items"
                        :key="item.q"
                        class="g11-faq__item"
                        :class="{ 'g11-faq__item--open': openFaq === index }"
                    >
                        <button
                            type="button"
                            class="g11-faq__trigger"
                            :aria-expanded="openFaq === index ? 'true' : 'false'"
                            @click="toggleFaq(index)"
                        >
                            <span class="g11-faq__question">{{ item.q }}</span>
                            <span class="g11-faq__chevron" aria-hidden="true" />
                        </button>
                        <div
                            v-show="openFaq === index"
                            :id="'g11-faq-panel-' + index"
                            class="g11-faq__panel"
                            role="region"
                        >
                            <p>{{ item.a }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- 9. Final CTA -->
        <section class="g11-section g11-section--cta">
            <div class="g11-wrap">
                <div class="g11-cta-card">
                    <h2 class="g11-cta-card__title">{{ finalCta.title }}</h2>
                    <p class="g11-cta-card__subtitle">{{ finalCta.subtitle }}</p>
                    <p class="g11-cta-card__tag">{{ finalCta.hashtag }}</p>
                    <div class="g11-cta-card__actions">
                        <router-link :to="coursesRoute" class="g11-btn g11-btn--primary g11-btn--lg">
                            <span>{{ finalCta.primaryCta }}</span>
                            <lucide-icon name="ArrowLeft" class="g11-btn__icon" />
                        </router-link>
                        <a
                            :href="whatsappUrl"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="g11-btn g11-btn--outline g11-btn--lg"
                        >
                            {{ finalCta.secondaryCta }}
                        </a>
                    </div>
                </div>
            </div>
        </section>
    </main>
</template>

<script>
import LucideIcon from '../LucideIcon.vue';
import {
    GRADE_11_HERO_IMAGES,
    grade11Hero,
    grade11IntroVideo,
    grade11Why,
    grade11Coverage,
    grade11Proof,
    grade11Journey,
    grade11Tools,
    grade11Calendar,
    grade11Parents,
    grade11Faq,
    grade11FinalCta,
} from './grade-11-data';

let heroAssetFallback = null;
try {
    heroAssetFallback = require('@/assets/welcome/ghonaim-hero.webp');
} catch (e) {
    heroAssetFallback = null;
}

export default {
    name: 'Grade11Landing',
    components: { LucideIcon },
    props: {
        coursesRoute: {
            type: Object,
            required: true,
        },
        whatsappUrl: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            hero: grade11Hero,
            introVideo: grade11IntroVideo,
            why: grade11Why,
            coverage: grade11Coverage,
            proof: grade11Proof,
            journey: grade11Journey,
            tools: grade11Tools,
            calendar: grade11Calendar,
            parents: grade11Parents,
            faq: grade11Faq,
            finalCta: grade11FinalCta,
            heroImageSrc: GRADE_11_HERO_IMAGES[0],
            heroImageFallbackIndex: 0,
            openFaq: null,
        };
    },
    computed: {
        youtubeEmbedUrl() {
            const id = this.introVideo && this.introVideo.youtubeId;
            if (!id) return '';
            return `https://www.youtube.com/embed/${id}?rel=0&modestbranding=1`;
        },
    },
    methods: {
        toggleFaq(index) {
            this.openFaq = this.openFaq === index ? null : index;
        },
        onHeroImageError() {
            const nextPublicIndex = this.heroImageFallbackIndex + 1;
            if (nextPublicIndex < GRADE_11_HERO_IMAGES.length) {
                this.heroImageFallbackIndex = nextPublicIndex;
                this.heroImageSrc = GRADE_11_HERO_IMAGES[nextPublicIndex];
                return;
            }
            if (heroAssetFallback) {
                this.heroImageSrc = heroAssetFallback;
            }
        },
    },
};
</script>
